import { css } from '@emotion/react';
import { colors } from '@/styles';
import {
    aggregateEvents,
    genAttributes,
    genOptionDisclaimer,
    genTimeframeText,
    MARKETPLACE_TIERS,
} from '@onward-delivery/core';
import onwardLogo from '@/assets/onwardlogotransparent.webp';
import { Grid, Checkbox, TextField, MenuItem, Tooltip } from '@material-ui/core';
import { OptionRow, Circle, SubTitle, Body1, Label } from './blocks';
import HandshakeIcon from '@mui/icons-material/Handshake';
import VerifiedUser from '@mui/icons-material/VerifiedUser';
import { formatInTimeZone } from 'date-fns-tz';
import zipcode_to_timezone from 'zipcode-to-timezone';

const dollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

const formatReservation = (reservation, order) => {
    const { zip } = genAttributes(order);
    const tz = zipcode_to_timezone.lookup(order[zip]) || 'America/New_York';

    if (reservation.del_window_start && reservation.del_window_end) {
        return [
            formatInTimeZone(new Date(reservation.del_window_start), tz, 'EEE, MMM d HH:mm'),
            formatInTimeZone(new Date(reservation.del_window_end), tz, 'HH:mm zzz'),
        ].join(' - ');
    }

    return reservation.delivery_date ? formatInTimeZone(new Date(reservation.delivery_date), tz, 'EEE, MMM d') : null;
};

export const Option = ({ override_attr, subtext, breakdown, overridesById, order, callbacks, tz, ...rest }) => {
    const isSelected = order[override_attr] === breakdown?._meta?.override_id;
    const client = overridesById[breakdown?._meta?.override_id]?.assign || {};

    const drivers = Object.values(
        (client?.carried_orders || []).reduce((acc, order) => {
            const aggregated = aggregateEvents(order.wh_events);

            if (order.shipper_rate_override?.algo_type === 'FRAYT' && aggregated.driver_email) {
                const driver = aggregated.driver_email
                    ? {
                          name: aggregated.driver_name,
                          id: aggregated.driver_email,
                      }
                    : {};

                acc[aggregated.driver_email] = driver;
            }

            return acc;
        }, {})
    );

    const reservationsByDate = Object.fromEntries(
        (breakdown?.reservations || []).map((reservation) => [
            new Date(reservation.delivery_date).toDateString(),
            reservation,
        ])
    );

    const price = breakdown.total_charge ? dollar.format(breakdown.total_charge) : '--';
    const label = client?.business_name || 'Onward';
    const logo = client?.profile_img || onwardLogo;
    const logoBg = client?.profile_img_color || colors.white.primary;

    let Icon, iconColor, iconTooltip;
    switch (overridesById[breakdown?._meta?.override_id]?.marketplace_tier) {
        case MARKETPLACE_TIERS.T1_INTEGRATIONS_ONLY:
            Icon = HandshakeIcon;
            iconColor = colors.golds.primary;
            iconTooltip = 'This is your partner carrier that is connected via Onward Integrations';
            break;
        case MARKETPLACE_TIERS.T2_INTEGRATIONS_ONLY:
        case MARKETPLACE_TIERS.T2_INTEGRATIONS_BILLING:
            Icon = VerifiedUser;
            iconColor = colors.greens.primary;
            iconTooltip = 'This carrier is available to you through the Onward Marketplace';
            break;
    }

    const onClick = () => {
        const hasMatchingIntegrationReservationId =
            order.integration_reservation_id &&
            Object.values(reservationsByDate).some(
                (reservation) => reservation.integration_reservation_id === order.integration_reservation_id
            );

        const matchingReservationByDate =
            order.delivery_date && reservationsByDate[new Date(order.delivery_date).toDateString()];

        let patch = {
            [override_attr]: breakdown?._meta?.override_id,
        };

        if (hasMatchingIntegrationReservationId) {
            // Don't clear reservation id if user clicks same option again
        } else if (matchingReservationByDate) {
            // Auto-set reservation data from matching delivery date
            const { delivery_date, del_window_start, del_window_end, integration_reservation_id } =
                matchingReservationByDate;
            patch = {
                ...patch,
                delivery_date,
                del_window_start,
                del_window_end,
                integration_reservation_id: integration_reservation_id || null,
            };
        } else {
            patch.integration_reservation_id = null;
        }

        const disclaimer = genOptionDisclaimer({ order, breakdown });
        if (!!disclaimer) {
            callbacks.setDisclaimer({
                order: {
                    ...order,
                    ...patch,
                },
                disclaimers: [disclaimer],
            });
        } else {
            callbacks.submitOrder({
                ...order,
                ...patch,
            });
        }
    };

    const onDriverSelect = (val) => {
        callbacks.submitOrder({
            ...order,
            preferred_driver_id: val === 'DEFAULT' ? null : val,
        });
    };

    const onReservationSelect = (reservation) => {
        const { delivery_date, del_window_start, del_window_end, integration_reservation_id } = reservation;
        callbacks.submitOrder({
            ...order,
            [override_attr]: breakdown?._meta?.override_id,
            delivery_date,
            del_window_start,
            del_window_end,
            integration_reservation_id: integration_reservation_id ? integration_reservation_id : null,
        });
    };

    return (
        <OptionRow selected={isSelected} className="option" {...rest}>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 0;
                    flex-basis: 0;
                    justify-content: center;
                    margin-right: 8px;
                `}
                onClick={onClick}
            >
                <Checkbox
                    checked={isSelected}
                    disabled={isSelected}
                    css={css`
                        &.Mui-checked {
                            color: ${colors.greens.primary};
                        }
                    `}
                />
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 0;
                    flex-basis: 0;
                    justify-content: center;
                    margin-right: 20px;
                `}
            >
                <Circle
                    css={css`
                        background: ${logoBg};

                        display: flex;
                        justify-content: center;
                        align-items: center;
                    `}
                >
                    <img
                        css={css`
                            object-fit: contain;
                            display: flex;
                            height: 38px;
                            width: 38px;
                        `}
                        src={logo}
                    />
                </Circle>
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 1;
                    flex-basis: 0;
                    justify-content: center;
                `}
            >
                <Grid
                    container
                    direction="row"
                    css={css`
                        align-items: center;
                    `}
                >
                    <Label
                        css={css`
                            color: #2b2b2b;
                        `}
                    >
                        {label}
                    </Label>
                    {Icon ? (
                        <Tooltip placement="top" title={iconTooltip}>
                            <Icon
                                css={css`
                                    margin-left: 12px;
                                    color: ${iconColor};
                                    width: 20px;
                                `}
                            />
                        </Tooltip>
                    ) : null}
                </Grid>
                {order.priced_as_middle_mile ? (
                    <Grid
                        container
                        direction="row"
                        css={css`
                            margin-bottom: 4px;
                        `}
                    >
                        <SubTitle
                            css={css`
                                color: #2b2b2b;
                            `}
                        >
                            {`${breakdown._meta.pu_location} - ${breakdown._meta.do_location}`}
                        </SubTitle>
                    </Grid>
                ) : null}
                <Grid container direction="row" onClick={onClick}>
                    <SubTitle
                        css={css`
                            color: #2b2b2b;
                        `}
                    >
                        {subtext}
                    </SubTitle>
                </Grid>
                {Object.keys(reservationsByDate)?.length > 0 ? (
                    <Grid
                        container
                        css={css`
                            margin-top: 16px;
                        `}
                    >
                        <Grid
                            container
                            direction="column"
                            css={css`
                                flex: 2;
                                flex-basis: 0;
                            `}
                        >
                            <TextField
                                variant="outlined"
                                select
                                fullWidth
                                size="small"
                                label="Reservation"
                                value={order.delivery_date ? new Date(order.delivery_date).toDateString() : ''}
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) => {
                                    const reservation = reservationsByDate[e.target.value];
                                    onReservationSelect(reservation);
                                }}
                                css={css`
                                    max-width: 300px;
                                `}
                                error={
                                    order.delivery_date &&
                                    !reservationsByDate[new Date(order.delivery_date).toDateString()]
                                }
                                helperText={
                                    order.delivery_date &&
                                    !reservationsByDate[new Date(order.delivery_date).toDateString()] ? (
                                        <span
                                            css={css`
                                                color: #f44336;
                                            `}
                                        >
                                            Invalid Date
                                        </span>
                                    ) : null
                                }
                            >
                                {Object.entries(reservationsByDate).map(([key, reservation]) => (
                                    <MenuItem key={key} value={key}>
                                        <Body1>{formatReservation(reservation, order)}</Body1>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                ) : null}
                {drivers.length > 0 ? (
                    <Grid
                        container
                        direction="row"
                        css={css`
                            margin-top: 16px;
                        `}
                    >
                        <Grid
                            container
                            direction="column"
                            css={css`
                                flex: 2;
                                flex-basis: 0;
                            `}
                        >
                            <TextField
                                variant="outlined"
                                select
                                fullWidth
                                size="small"
                                label="Preferred Driver"
                                value={order.preferred_driver_id || 'DEFAULT'}
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) => {
                                    const id = e.target.value;
                                    onDriverSelect(id);
                                }}
                                css={css`
                                    width: 100%;
                                `}
                                error={order.preferred_driver_id && !breakdown._meta.preferred_driver_accepted}
                                helperText={
                                    order.preferred_driver_id && !breakdown._meta.preferred_driver_accepted ? (
                                        <span
                                            css={css`
                                                color: #f44336;
                                            `}
                                        >
                                            Invalid Driver
                                        </span>
                                    ) : null
                                }
                            >
                                <MenuItem key="DEFAULT" value="DEFAULT">
                                    <Body1>Marketplace</Body1>
                                </MenuItem>
                                {drivers.map(({ name, id }) => (
                                    <MenuItem key={id} value={id}>
                                        <Body1>{name}</Body1>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid
                            container
                            direction="column"
                            css={css`
                                flex: 1;
                                flex-basis: 0;
                            `}
                        />
                    </Grid>
                ) : null}
            </Grid>
            <Grid
                container
                direction="column"
                css={css`
                    flex: 0;
                    flex-basis: 0;
                    justify-content: center;
                `}
                onClick={onClick}
            >
                <Label
                    css={css`
                        color: #2b2b2b;
                        white-space: nowrap;
                    `}
                >
                    {`Price: ${price}`}
                </Label>
            </Grid>
        </OptionRow>
    );
};
